import type { SnapshotLevel } from "@healthos/shared";
import { ArrowBendDownRight, ArrowRight, Check } from "@phosphor-icons/react";
import { type ReadingParts, TrendToMove } from "./snapshot-term";
import { SNAPSHOT_BEHAVIOURS } from "./snapshot-tokens";

function accentOf(behaviour: string): string {
  return SNAPSHOT_BEHAVIOURS.find((b) => b.id === behaviour)?.color ?? "var(--color-teal-700)";
}

/** The level card: days into this level, the focus area, and the week's days. */
export function SnapshotLevelCard({
  level,
  evidence,
  onOpen,
}: {
  level: SnapshotLevel;
  /** What the week's sharpest reading says about this habit, when there is one. */
  evidence?: ReadingParts | null;
  /** Opens the level in full. The arrow only shows when there is somewhere to go. */
  onOpen?: () => void;
}) {
  const pct = Math.round((level.daysThisLevel / level.daysPerLevel) * 100);

  const Root = onOpen ? "button" : "div";
  return (
    <Root
      className={`snap-fa${onOpen ? " is-open" : ""}`}
      style={{ ["--accent" as string]: accentOf(level.behaviour) }}
      {...(onOpen
        ? { type: "button" as const, onClick: onOpen, "aria-label": `Open ${level.habit}` }
        : {})}
    >
      <span className="snap-fa-ring" style={{ ["--pct" as string]: `${pct}%` }}>
        <span className="snap-fa-n">{level.daysThisLevel}</span>
        <span className="snap-fa-lv">Lv {level.level}</span>
      </span>
      <span className="snap-fa-body">
        <span className="snap-fa-top">
          <span className="snap-fa-name">{level.habit}</span>
          {onOpen ? <ArrowRight size={14} className="snap-fa-go" aria-hidden="true" /> : null}
        </span>
        {evidence ? (
          <span className="snap-fa-first">
            <ArrowBendDownRight size={13} aria-hidden="true" />
            <TrendToMove parts={evidence} plain={!!onOpen} />
          </span>
        ) : null}
        <span className="snap-fa-days">{level.daysThisWeek} of 7 days this week</span>
        <span className="snap-fa-prog">
          <span className="snap-fa-track">
            <i style={{ width: `${pct}%` }} />
          </span>
          <span className="snap-fa-next">
            {level.daysThisLevel}/{level.daysPerLevel} days to Lv {level.level + 1}
          </span>
        </span>
      </span>
    </Root>
  );
}

/**
 * The ladder: what is being practised now, the level after it, and the day
 * count that makes it a habit. Every rung counts the member's own days.
 */
export function SnapshotLadder({
  level,
  evidence,
}: {
  level: SnapshotLevel;
  /** The trend this level exists to move, in the member's own numbers. */
  evidence?: ReadingParts | null;
}) {
  const toNext = Math.max(0, level.daysPerLevel - level.daysThisLevel);
  const toHabit = Math.max(0, level.habitTargetDays - level.days);
  const rungs = [
    {
      key: "now",
      title: level.habit,
      sub: `This week, ${level.daysThisWeek} of 7 days`,
      state: "active" as const,
      first: evidence ? <TrendToMove parts={evidence} /> : null,
    },
    {
      key: "next",
      title: `Level ${level.level + 1}`,
      sub: toNext === 1 ? "1 more day of practice" : `${toNext} more days of practice`,
      state: "next" as const,
      first: null,
    },
    {
      key: "habit",
      title: "The habit holds on its own",
      sub: level.habitEstablished
        ? `Established, ${level.days} days practised`
        : `${toHabit} more days to ${level.habitTargetDays}`,
      state: level.habitEstablished ? ("done" as const) : ("next" as const),
      first: null,
    },
  ];

  return (
    <div
      className="snap-ladder-block"
      style={{ ["--accent" as string]: accentOf(level.behaviour) }}
    >
      <div className="snap-section-head">
        <h2>Levelling up {level.label.toLowerCase()}</h2>
        <span className="hint">
          {level.daysThisLevel} of {level.daysPerLevel} to level {level.level + 1}
        </span>
      </div>
      <ol className="snap-ladder">
        {rungs.map((rung, i) => (
          <li key={rung.key} className={`is-${rung.state}`}>
            {rung.first ? (
              <span className="snap-ld-first">
                <ArrowBendDownRight size={13} aria-hidden="true" />
                {rung.first}
              </span>
            ) : null}
            <span className="snap-ld-m">
              {rung.state === "done" ? <Check size={13} weight="bold" /> : i + 1}
            </span>
            <span className="snap-ld-b">
              <span className="snap-ld-t">{rung.title}</span>
              <span className="snap-ld-s">{rung.sub}</span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
